import { MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEntityDetail, useRestoreMutation } from "../../../api/BaseEntityApi";
import { MoreDropdown } from "../../../common/buttons/MoreDropdown/MoreDropdown";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import useIntersectionObserver from "../../../common/helperfunctions/useIntersectionObserver";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { ELNDeleteConfirmationModal } from "../../ELNCreateOrEditForms/ELNDeleteConfirmationModal";
import { useELNRoutes } from "../../ELNRouter/useELNRoutes";
import { TextEditor } from "../../common/TextEditor/TextEditor";
import { LAB_NOTEBOOK_TOC_QUERY_KEY } from "../../common/tiles/LabNotebookTocTile/LabNotebookTocTile";
import { LabNotebookEntry, LabNotebookEntryFilters } from "../../types/LabNotebookEntry";
import styles from "./LabNotebookEntryViewOnly.module.css";
import { QuickEditWrapper } from "../../../common/forms/QuickEdit/common/QuickEditWrapper";
import { QuickEditLabNotebookEntryForm } from "../../../common/forms/QuickEdit/forms/QuickEditLabNotebookEntry";
import { GetPersons } from "../../../common/misc/EntityRenders/EntityRenderer";
import { getNotebookEntryStringLabel } from "../LabNotebookEntry";
import { GenericEntityHistoryModal } from "../../../Entities/GenericComponents/common/GenericEntityHistoryModal";
import { PrintCompatibleLink } from "../../../common/misc/PrintCompatibleLink.tsx/PrintCompatibleLink";
import { GenericEntityExportButton } from "../../../Entities/GenericComponents/common/GenericEntityExportButton";

export interface LabNotebookEntryViewOnlyPageControls {
  onClick: (e: any) => void;
  icon?: string;
  label?: string;
  tooltip?: string;
  color?:
    | "primary"
    | "secondary"
    | "warning"
    | "danger"
    | "soft-primary"
    | "soft-secondary"
    | "soft-warning"
    | "soft-danger"
    | "ghost-primary"
    | "ghost-secondary"
    | "ghost-warning"
    | "ghost-danger";
}

export const LabNotebookEntryViewOnly = ({
  entry,
  labNotebookExperimentId,
  remirrorContextRef,
  ignoreIntersectionObserver = false,
  printMode = false,
  useEntry = false,
  showModifiedState = false,
  showViewButton,
  overlay,
  ignoreStickyStyles = false,
  handleEntryLoadingStateChange,
}: {
  entry?: LabNotebookEntry;
  labNotebookExperimentId?: number;
  remirrorContextRef?: MutableRefObject<any>;
  ignoreIntersectionObserver?: boolean;
  printMode?: boolean;
  useEntry?: boolean;
  showModifiedState?: boolean;
  showViewButton?: boolean;
  overlay?: ReactNode;
  ignoreStickyStyles?: boolean;
  handleEntryLoadingStateChange?: (entryId: LabNotebookEntry["id"], isLoading: boolean) => void;
}) => {
  const { setELNRoute, getELNRoute } = useELNRoutes();
  const queryClient = useQueryClient();

  const [showEditLabNotebookEntryModal, setShowEditLabNotebookEntryModal] = useState<boolean>(false);
  const [showDeleteLabNotebookEntryModal, setShowDeleteLabNotebookEntryModal] = useState<boolean>(false);
  const [headerIsPinned, setHeaderIsPinned] = useState<boolean>(false);

  const { mutate } = useRestoreMutation("lab_notebook_entries");

  const { data } = useEntityDetail<LabNotebookEntry, LabNotebookEntryFilters>(
    "lab_notebook_entries",
    entry?.id || 0,
    { includeContent: true },
    {
      enabled: entry?.id !== undefined && !useEntry,
    }
  );

  const currentEntry = useMemo(() => {
    if (useEntry) return entry;
    return data;
  }, [data, entry, useEntry]);

  const ref = useRef<HTMLDivElement>(null);
  const observer = useIntersectionObserver(ref, { threshold: [1] });

  useEffect(() => {
    if (observer && observer.intersectionRatio < 1) {
      setHeaderIsPinned(true);
    } else {
      setHeaderIsPinned(false);
    }
  }, [observer]);

  const header = useMemo(() => {
    if (!currentEntry) return null;
    return (
      <div
        className={styles.pageHeader}
        ref={ref}
        style={
          headerIsPinned && !printMode && !ignoreStickyStyles
            ? {
                boxShadow: "1px 1px 1px 1px var(--gray-300)",
                borderBottomLeftRadius: "5px",
                borderBottomRightRadius: "5px",
                backgroundColor: "var(--gray-50)",
                padding: "2px 10px",
              }
            : {}
        }
      >
        <div
          style={
            !!printMode
              ? { display: "block", overflow: "hidden", alignItems: "center" }
              : {
                  display: "grid",
                  gridTemplateColumns:
                    "minmax(100px, max-content) max-content minmax(100px, max-content) max-content minmax(100px, max-content)",
                  overflow: "hidden",
                  alignItems: "center",
                }
          }
        >
          <PrintCompatibleLink
            to={getELNRoute({
              labNotebookId: currentEntry.labNotebook.id,
            })}
            style={{
              textDecoration: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              ...(printMode ? { display: "block", marginBottom: "2px" } : {}),
            }}
            isPrinting={printMode}
            title={currentEntry.labNotebook.name}
          >
            <div className="flex align-center gap-5">
              <LucideIcon name="book" color="var(--primary)" /> {currentEntry.labNotebook.name}
            </div>
          </PrintCompatibleLink>
          {!printMode && (
            <div>
              <LucideIcon name="chevron-right" />
            </div>
          )}
          <PrintCompatibleLink
            to={getELNRoute({
              labNotebookId: currentEntry.labNotebook.id,
              labNotebookExperimentId: currentEntry.labNotebookExperiment.id,
            })}
            style={{
              textDecoration: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              ...(printMode ? { display: "block", marginBottom: "2px" } : {}),
            }}
            isPrinting={printMode}
            title={currentEntry.labNotebookExperiment.name}
          >
            <div className="flex align-center gap-5">
              <LucideIcon name="file-text" color="var(--secondary)" /> {currentEntry.labNotebookExperiment.name}
            </div>
          </PrintCompatibleLink>
          {!printMode && (
            <div>
              <LucideIcon name="chevron-right" />
            </div>
          )}
          <PrintCompatibleLink
            to={getELNRoute({
              labNotebookId: currentEntry.labNotebook.id,
              labNotebookExperimentId: currentEntry.labNotebookExperiment.id,
              labNotebookEntryId: currentEntry.id,
            })}
            style={{
              textDecoration: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              ...(printMode ? { display: "block" } : {}),
            }}
            isPrinting={printMode}
            title={getNotebookEntryStringLabel({ entry: currentEntry })}
          >
            <div className="flex align-center gap-5">
              <LucideIcon name="pencil" color="var(--success)" /> {getNotebookEntryStringLabel({ entry: currentEntry })}
              {!!entry?.isDeleted && <LucideIcon name="trash" color="var(--warning)" style={{ margin: 0 }} />}
            </div>
          </PrintCompatibleLink>
        </div>
        {!printMode && (
          <div className="flex align-center gap-5">
            {showViewButton && (
              <button
                className="btn btn-ghost-secondary btn-xs"
                data-toggle="tooltip"
                title="View entry"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setELNRoute({
                    labNotebookId: currentEntry.labNotebook.id,
                    labNotebookExperimentId: currentEntry.labNotebookExperiment.id,
                    labNotebookEntryId: currentEntry.id,
                    mode: "view",
                    filters: !!currentEntry.isDeleted ? { includeSoftDeleted: true } : undefined,
                  });
                }}
              >
                <LucideIcon name="maximize" />
                <span> View</span>
              </button>
            )}
            <button
              disabled={!currentEntry.permissions?.canEdit || !!currentEntry.isDeleted}
              className="btn btn-default btn-xs"
              data-toggle="tooltip"
              title={
                !currentEntry.permissions?.canEdit || !!currentEntry.isDeleted
                  ? "No permissions to edit this entry"
                  : "Edit entry content"
              }
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setELNRoute({
                  labNotebookId: currentEntry.labNotebook.id,
                  labNotebookExperimentId: currentEntry.labNotebookExperiment.id,
                  labNotebookEntryId: currentEntry.id,
                  mode: "edit",
                });
              }}
            >
              <LucideIcon name="square-pen" />
              <span> Edit</span>
            </button>
            <MoreDropdown
              btn="btn btn-ghost-secondary btn-xs"
              icon={<LucideIcon name="ellipsis-vertical" />}
              direction="down"
              drop="right"
            >
              {entry && !entry?.isDeleted && (
                <button
                  disabled={!currentEntry.permissions?.canEdit || !!currentEntry.isDeleted}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowEditLabNotebookEntryModal(true);
                  }}
                  style={{ width: "100%", textAlign: "left", padding: "5px 10px" }}
                  className="btn btn-ghost-secondary btn-xs"
                >
                  <LucideIcon name="square-pen" /> Edit entry details
                </button>
              )}
              {!!entry?.id && (
                <GenericEntityExportButton
                  entityTypeId="notebookEntries"
                  entity={entry}
                  componentProps={{ style: { width: "100%", textAlign: "left", padding: "5px 10px" } }}
                />
              )}
              <GenericEntityHistoryModal
                entityTypeId="notebookEntries"
                entity={entry}
                componentProps={{
                  triggerComponent: ({ setIsOpen }) => (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsOpen(true);
                      }}
                      style={{ width: "100%", textAlign: "left", padding: "5px 10px" }}
                      className="btn btn-ghost-secondary btn-xs"
                    >
                      <LucideIcon name="clock" /> Show entry history
                    </button>
                  ),
                }}
              />
              {entry && !!entry.isDeleted ? (
                <>
                  <button
                    disabled={!currentEntry.permissions?.canEdit}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      mutate(
                        { id: entry.id },
                        {
                          onSuccess: () => {
                            queryClient.invalidateQueries([LAB_NOTEBOOK_TOC_QUERY_KEY]);
                          },
                        }
                      );
                    }}
                    style={{ width: "100%", textAlign: "left", padding: "5px 10px" }}
                    className="btn btn-ghost-success btn-xs"
                  >
                    <LucideIcon name="archive-restore" /> Restore entry
                  </button>
                </>
              ) : (
                <button
                  disabled={!currentEntry.permissions?.canEdit}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowDeleteLabNotebookEntryModal(true);
                  }}
                  style={{ width: "100%", textAlign: "left", padding: "5px 10px" }}
                  className="btn btn-ghost-warning btn-xs btn-block"
                >
                  <LucideIcon name="trash" /> Trash entry
                </button>
              )}
            </MoreDropdown>
          </div>
        )}
      </div>
    );
  }, [
    currentEntry,
    entry,
    getELNRoute,
    headerIsPinned,
    ignoreStickyStyles,
    mutate,
    printMode,
    queryClient,
    setELNRoute,
    showViewButton,
  ]);

  // Loading state for the editor
  const handleEditorLoadingStateChange = useCallback(
    (isLoading: boolean) => {
      !!entry?.id && handleEntryLoadingStateChange?.(entry.id, isLoading);
    },
    [entry?.id, handleEntryLoadingStateChange]
  );

  if (!currentEntry) return null;
  return (
    <>
      <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <div id={`ELN_ENTRY_${currentEntry.id}`} className={printMode ? styles.dinA4PaperPrint : styles.dinA4Paper}>
          {header}
          {entry && showModifiedState && (
            <div
              className="flex align-center gap-5"
              style={{
                width: "100%",
                flexWrap: "wrap",
                fontSize: "12px",
              }}
            >
              <div className="flex align-center gap-5" style={{ maxWidth: "max-content", width: "100%" }}>
                <LucideIcon name="save" color="var(--gray-600)" />
                <DateTimeRenderer date={entry.modifiedOn} includeElapsed />
              </div>
              <div className="flex row-nowrap align-center gap-5">
                by <GetPersons persons={[entry.modifiedBy]} />
              </div>
            </div>
          )}
          <hr style={{ margin: "5px 0", marginBottom: "10px" }} />
          <div style={{ position: "relative", height: "fit-content" }}>
            {!!overlay && (
              <div style={{ position: "absolute", width: "100%", height: "100%", zIndex: 1 }}>{overlay}</div>
            )}
            <TextEditor
              editable={false}
              remirrorContextRef={remirrorContextRef}
              initialContent={currentEntry.content}
              save={() => {}}
              ignoreIntersectionObserver={ignoreIntersectionObserver}
              handleEditorLoadingStateChange={handleEditorLoadingStateChange}
            />
          </div>
        </div>
      </div>

      <>
        {/* Edit */}
        {showEditLabNotebookEntryModal && (
          <QuickEditWrapper<LabNotebookEntry>
            showModal={showEditLabNotebookEntryModal}
            setShowModal={setShowEditLabNotebookEntryModal}
            onEdit={(editedLabNotebook) => {
              queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
              queryClient.invalidateQueries({ queryKey: ["lab_notebook_entries"] });
            }}
          >
            {(props) => <QuickEditLabNotebookEntryForm {...props} initialValues={currentEntry} />}
          </QuickEditWrapper>
        )}

        {/* Delete */}
        <ELNDeleteConfirmationModal
          isOpen={showDeleteLabNotebookEntryModal}
          setIsOpen={setShowDeleteLabNotebookEntryModal}
          name={getNotebookEntryStringLabel({ entry: currentEntry })}
          id={currentEntry.id}
          entityInfo={{
            "Entry": getNotebookEntryStringLabel({ entry: currentEntry }),
            "Part of": currentEntry.labNotebook.name + " - " + currentEntry.labNotebookExperiment.name,
          }}
          resource="lab_notebook_entries"
          onDelete={() => {
            queryClient.invalidateQueries({ queryKey: [LAB_NOTEBOOK_TOC_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: ["lab_notebooks"] });
            queryClient.invalidateQueries({ queryKey: ["lab_notebook_experiments"] });
            queryClient.invalidateQueries({ queryKey: ["lab_notebook_entries"] });
          }}
          description="Proceeding will move the selected entry into trash."
          deletePermanently={false}
        />
      </>
    </>
  );
};
